/// <reference path="../../../../types/cordova-plugins.d.ts" />
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import theme from "../../../muiTheme";
import SuppliesBillsStateContent from "../../SuppliesBillsStateContent/SuppliesBillsStateContent";
import "./ManagementsRequestState.scss";
import { useTranslation } from "react-i18next";
import {
  ManagementDetail,
  ManagementRequestInfo,
  RequestStateData,
} from "../../../core/models/managements/managements.model";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_OPEN,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_STATE_PENDING_DISTRIBUTION,
  MANAGEMENTS_STATE_REJECTED,
  MANAGEMENTS_STATE_RESOLVED,
} from "../../../core/constants/constants";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useNavigate } from "react-router-dom";
import { getManagementStateData } from "../../../utils/components/managements/managements.util";

interface CustomWindow extends Window {
  PreviewAnyFile?: {
    previewPath: (
      successCallback: (win: any) => void,
      errorCallback: (error: any) => void,
      path: string,
      options: { name: string }
    ) => void;
  };
}
declare let window: CustomWindow;

interface ManagementsRequestStateProps {
  state: string;
  title: string;
  description?: string;
  showManagementRequestInfo?: boolean;
  descriptionExtra?: string;
  managementRequestInfo?: ManagementRequestInfo | ManagementDetail;
  isLoading?: boolean;
  type?: string;
  loadingFiles?: boolean;
  files?: any[];
  isSupplies?: boolean;
}

const ManagementsRequestState = ({
  state,
  title,
  description,
  descriptionExtra,
  managementRequestInfo,
  showManagementRequestInfo = false,
  isLoading = false,
  type,
  loadingFiles = false,
  files,
  isSupplies = false,
}: ManagementsRequestStateProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getBorderColor = () => {
    switch (state) {
      case MANAGEMENTS_STATE_OPEN:
        return theme.palette.info.main;
      case MANAGEMENTS_STATE_RESOLVED:
        return theme.palette.success.main;
      case MANAGEMENTS_STATE_REJECTED:
        return theme.palette.error.main;
      case MANAGEMENTS_STATE_ERROR:
      case "":
        return theme.palette.grey[300];
      default:
        return theme.palette.info.main;
    }
  };

  const requestStateData: RequestStateData[] = [
    {
      id: "refferenceNumber",
      label:
        type && type === "servicios"
          ? "Servicios.Número de servicio"
          : "Gestiones.Número de gestión",
    },
    {
      id: "registrarionDate",
      label: "Fecha de registro",
    },
    {
      id: "closingDate",
      label: "Fecha de resolución",
      showCondition: state === MANAGEMENTS_STATE_RESOLVED,
    },
  ];

  const showRequestInfo =
    showManagementRequestInfo && state !== MANAGEMENTS_STATE_ERROR;
  const showButton =
    [MANAGEMENTS_STATE_ERROR, MANAGEMENTS_STATE_REJECTED].includes(state) &&
    !isLoading;
  const isErrorState = state === MANAGEMENTS_STATE_ERROR;

  const onDownloadFileAction = async (file: {
    DocumentoData: "string";
    FileName: "string";
  }) => {
    const { DocumentoData, FileName } = file;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const filename = FileName || "download.pdf";

    try {
      const url = `data:application/pdf;base64,${DocumentoData}`;

      if (isMobile && typeof cordova !== "undefined") {
        if (isIOS && cordova.InAppBrowser) {
          cordova.InAppBrowser.open(url, "_system");
        } else {
          window.PreviewAnyFile?.previewPath(
            (win) => console.log("open status", win),
            (error) => console.error("open failed", error),
            url,
            { name: filename }
          );
        }
      } else {
        // Convertir base64 a Blob para descarga
        const blob = await (await fetch(url)).blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error("Error downloading the file", error);
    }
  };

  return (
    <Grid
      container
      justifyContent={isSupplies ? "flex-start" : "center"}
      paddingTop={{ xs: "1rem", md: isSupplies ? "1rem" : "3rem" }}
      paddingX={{ xs: "1rem", md: isSupplies ? "2rem" : "3rem" }}
    >
      <Grid item position={"relative"} md={8} lg={7} flex={1}>
        <Box
          component="div"
          className={isSupplies ? "" : "managements-update-resolution"}
          borderTop={isSupplies ? "" : `1rem solid ${getBorderColor()}`}
          padding={{ xs: isSupplies ? "0" : "3rem", md: isSupplies ? "1rem 0" : "3rem" }}
          marginBottom={{ xs: isSupplies ? 0 : "3rem", md: 0 }}
        >
          {isLoading ? (
            <LoadingComponentComponent
              isLoading={isLoading}
              position="relative"
              height={"200px"}
              backgroundColor="transparent"
            />
          ) : (
            <>
              {state !== MANAGEMENTS_STATE_ERROR && (
                <SuppliesBillsStateContent
                  state={state}
                  formatText={getManagementStateData}
                />
              )}
              <Typography
                component="h3"
                variant="h3"
                paddingTop="1.5rem"
                paddingBottom="2rem"
              >
                {t(title)}
              </Typography>
              {showRequestInfo && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  gap={{ xs: "0.5rem", md: "2rem" }}
                  paddingBottom={"2rem"}
                >
                  {requestStateData.map(
                    ({ id, label, showCondition = true }) =>
                      showCondition && (
                        <Stack key={id} direction={"column"}>
                          <Typography
                            component="p"
                            variant="body2"
                            color={theme.palette.grey[500]}
                          >
                            {t(label)}
                          </Typography>
                          <Typography component="h3" variant="h3">
                            {managementRequestInfo?.[id]}
                          </Typography>
                        </Stack>
                      )
                  )}
                </Stack>
              )}
              <Stack gap={"1rem"}>
                {managementRequestInfo?.solutionText?.map((text, index) => (
                  <Typography
                    component="p"
                    variant="body2"
                    color={theme.palette.grey[700]}
                    key={index}
                  >
                    {text.trim()}
                  </Typography>
                ))}
                {description && (
                  <Typography
                    component="p"
                    variant="body2"
                    color={theme.palette.grey[700]}
                  >
                    {t(description)}
                  </Typography>
                )}
                {state === MANAGEMENTS_STATE_PENDING ||
                  (state === MANAGEMENTS_STATE_OPEN && descriptionExtra && (
                    <Typography
                      component="p"
                      variant="body2"
                      color={theme.palette.grey[700]}
                    >
                      {t(descriptionExtra)}
                    </Typography>
                  ))}
              </Stack>
            </>
          )}
          {showButton && (
            <Box paddingTop={{ xs: "1rem", md: "1.5rem" }}>
              <ButtonElement
                secondary={isErrorState}
                onClick={() =>
                  isErrorState
                    ? navigate(process.env.REACT_APP_MANAGEMENT!)
                    : {}
                }
              >
                {t(
                  isErrorState
                    ? "Volver al inicio"
                    : "Contacta con atención al cliente"
                )}
              </ButtonElement>
            </Box>
          )}

          {loadingFiles && (
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                columnGap: "16px",
                alignItems: "center",
              }}
              paddingTop={"1.5rem"}
            >
              <CircularProgress color="primary" size={"1.5rem"} />
              <Typography component="p" variant="body1">
                {t("Gestiones.Cargando documentos, por favor espera...")}
              </Typography>
            </Stack>
          )}
          {files && files.length > 0 && (
            <Stack
              paddingTop={"1.5rem"}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography component="p" paddingBottom={".5rem"}>
                {t("Gestiones.Descarga documentos")}
              </Typography>
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    paddingBottom: index === files.length - 1 ? "0" : ".3rem",
                  }}
                >
                  <ButtonElement
                    link={true}
                    onClick={() => onDownloadFileAction(file)}
                    styles={{ textAlign: "left" }}
                  >
                    <Typography
                      component="span"
                      sx={{ marginRight: "0.5rem", verticalAlign: "middle" }}
                      className="t-icon-download"
                      aria-hidden="true"
                    ></Typography>
                    {file.FileName}
                  </ButtonElement>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ManagementsRequestState;
